import { create } from 'zustand';
import LoginModel from '@/models/services/LoginModel';
import OktaTokenModel from '@/models/services/OktaTokenModel';
import LogoutModel from '@/models/services/LogoutModel';
import TokenRefreshModel from '@/models/services/TokenRefreshModel';
import apiConnector from '@/models/connectors/apiConnector';
import CurrentUserModel from '@/models/services/CurrentUserModel';
import { loadReferences } from '@/stores/useReferenceOptions';
import {
  type AuthUser,
  type Session,
  type TokenResponse,
  SESSION_STORAGE_KEY,
  createSession,
  getTokenUrn,
  isAuthPath,
  isTokenExpiring,
  readStoredSession,
  storeSession,
} from '@/security/session';

export type { AuthUser, TokenPayload } from '@/security/session';

/** Verrou partagé entre onglets : un seul refresh à la fois (le refresh token est à usage unique) */
const REFRESH_LOCK_NAME = 'teiji.token-refresh';

interface AuthStore {
  /** Session (JWT + refresh token) : connecté tant qu'elle existe ; le JWT ne porte que l'URN (owner.urn) */
  session: Session | null;
  /**
   * Utilisateur connecté, chargé par GET /users/me (après la connexion et au rechargement de la page) :
   * nom, email, rôle et passwordDefined. null tant qu'il n'est pas chargé.
   */
  user: AuthUser | null;
  /** /users/me n'a pas pu être joint (API injoignable) : RequireAuth propose de réessayer */
  loadError: boolean;
  /** Charge l'utilisateur connecté (GET /users/me) */
  loadCurrentUser: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  /** Fin de la connexion Okta : échange le code à usage unique du retour d'Okta (cf. src/security/sso.ts) */
  loginWithOkta: (code: string, verifier: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Après définition du mot de passe local */
  markPasswordDefined: () => void;
  /** Après modification de son profil */
  updateUser: (changes: Partial<Pick<AuthUser, 'firstName' | 'lastName'>>) => void;
}

const initialSession = readStoredSession();

export const useAuthStore = create<AuthStore>(() => ({
  session: initialSession,
  user: null,
  loadError: false,

  login: async (email, password) => {
    const { model } = await new LoginModel().save({ email, password }, { silentError: true });
    await openSession(model);
  },

  loginWithOkta: async (code, verifier) => {
    const { model } = await new OktaTokenModel().save({ code, verifier }, { silentError: true });
    await openSession(model);
  },

  loadCurrentUser: () => {
    loadInFlight ??= doLoadCurrentUser().finally(() => {
      loadInFlight = null;
    });
    return loadInFlight;
  },

  updateUser: (changes) => {
    const { user } = useAuthStore.getState();
    if (user) useAuthStore.setState({ user: { ...user, ...changes } });
  },

  markPasswordDefined: () => {
    const { user } = useAuthStore.getState();
    if (user) useAuthStore.setState({ user: { ...user, passwordDefined: true } });
  },

  logout: async () => {
    const refreshToken = useAuthStore.getState().session?.refreshToken;
    applySession(null);

    // Invalide le refresh token côté API ; sans effet sur la déconnexion locale si l'appel échoue
    if (refreshToken) {
      await new LogoutModel().save({ refresh_token: refreshToken }, { silentError: true }).catch(() => undefined);
    }
  },
}));

/** Session reçue de /login ou /sso/okta/token ; comme au rechargement, l'utilisateur vient de /users/me */
async function openSession(response: TokenResponse) {
  applySession(createSession(response));
  await useAuthStore.getState().loadCurrentUser();
  if (!useAuthStore.getState().user) throw new Error('Session refusée');
}

function applySession(session: Session | null) {
  storeSession(session);
  syncSession(session);
}

/** Met à jour la session ; l'utilisateur chargé est oublié si la session se termine ou change d'identité */
function syncSession(session: Session | null) {
  const { user } = useAuthStore.getState();
  const sameUser = !!session && !!user && user.urn === getTokenUrn(session.token);
  useAuthStore.setState({ session, user: sameUser ? user : null, ...(session ? {} : { loadError: false }) });
}

let loadInFlight: Promise<void> | null = null;

async function doLoadCurrentUser() {
  if (!useAuthStore.getState().session) return;
  useAuthStore.setState({ loadError: false });

  try {
    // appRole arrive réduit à son id : sa référence (rôles du menu) vient de la liste des rôles
    const [{ model }] = await Promise.all([
      new CurrentUserModel().fetch({ silentError: true }),
      loadReferences('appRoles'),
    ]);
    const { session } = useAuthStore.getState(); // éventuellement rafraîchie par la requête
    const urn = getTokenUrn(session?.token ?? null);
    if (!urn) return; // déconnecté entre-temps (refresh refusé…)

    useAuthStore.setState({
      user: {
        urn,
        email: model.email,
        firstName: model.firstName,
        lastName: model.lastName,
        roles: model.securityRoles,
        passwordDefined: model.passwordDefined,
      },
    });
  } catch (error) {
    // API injoignable : ni connecté ni déconnecté, RequireAuth propose de réessayer
    if (error instanceof TypeError) {
      useAuthStore.setState({ loadError: true });
      return;
    }
    // Refusé (compte désactivé, session révoquée, ancien jeton…) : fin de session
    applySession(null);
  }
}

let refreshInFlight: Promise<void> | null = null;

/**
 * Rafraîchit le JWT avec le refresh token. Les appels simultanés partagent le même refresh :
 * le refresh token étant à usage unique, deux refresh en parallèle déconnecteraient l'utilisateur.
 */
export function refreshSession(): Promise<void> {
  refreshInFlight ??= withRefreshLock(doRefresh).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function withRefreshLock(callback: () => Promise<void>): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK_NAME, callback);
  }
  return callback();
}

async function doRefresh() {
  // Relu dans le stockage : un autre onglet a pu rafraîchir (ou déconnecter) pendant l'attente du verrou
  const session = readStoredSession();
  if (!session?.refreshToken) {
    applySession(null);
    return;
  }
  if (!isTokenExpiring(session)) {
    applySession(session);
    return;
  }

  try {
    const { model } = await new TokenRefreshModel().save(
      { refresh_token: session.refreshToken },
      { silentError: true },
    );
    applySession(createSession(model));
  } catch (error) {
    // API injoignable : la session est conservée, la requête échouera et un prochain essai refera le refresh
    if (error instanceof TypeError) throw error;
    // Refresh token expiré, déjà utilisé ou révoqué (compte désactivé) : fin de session.
    // C'est le seul point de déconnexion forcée : model-core ne transmet pas le status HTTP des erreurs,
    // une 401 en cours de session n'est donc pas détectée (cas rare avec le refresh préventif)
    applySession(null);
  }
}

/**
 * En-tête d'authentification d'une requête à l'API (header de la plateforme Stonecode), après avoir rafraîchi le JWT
 * s'il expire bientôt ; vide sans session. Ajouté à chaque requête de model-core, et aux envois multipart (postMultipart)
 */
export async function authorizationHeaders(): Promise<Record<string, string>> {
  let { session } = useAuthStore.getState();
  if (session && isTokenExpiring(session)) {
    await refreshSession();
    session = useAuthStore.getState().session;
  }

  return session ? { 'X-Platform-Authorization': `Bearer ${session.token}` } : {};
}

apiConnector.onRequestStart(async (_connector, path, options) => {
  if (isAuthPath(path)) return;

  options.headers = { ...options.headers, ...(await authorizationHeaders()) };
});

// Connexion, refresh et déconnexion faits dans un autre onglet
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== SESSION_STORAGE_KEY && event.key !== null) return;

    syncSession(readStoredSession());
  });
}
